import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, RefreshCw, AlertCircle } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { AdminTicket, TicketCounts, TicketStatus } from '@/types';

const STATUS_TABS: { label: string; value: TicketStatus | 'all' }[] = [
  { label: 'All',      value: 'all' },
  { label: 'Open',     value: 'open' },
  { label: 'Pending',  value: 'pending' },
  { label: 'Closed',   value: 'closed' },
  { label: 'Resolved', value: 'resolved' },
];

const STATUS_BADGE: Record<string, string> = {
  open:     'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  pending:  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  closed:   'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  resolved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
};

function relativeTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function Tickets() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TicketStatus | 'all'>('all');
  const [tickets, setTickets]     = useState<AdminTicket[]>([]);
  const [counts, setCounts]       = useState<TicketCounts | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  async function load(status: TicketStatus | 'all') {
    setLoading(true); setError(null);
    try {
      const res = await api.listAdminTickets(status === 'all' ? undefined : status);
      setTickets(res.tickets); setCounts(res.counts);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load tickets.');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(activeTab); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-6xl">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Support Tickets</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">Manage and respond to customer requests</p>
        </div>
        <button
          onClick={() => load(activeTab)}
          disabled={loading}
          className="self-start sm:self-auto flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary cards — 2 cols on mobile, 4 on sm+ */}
      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {([['Open', 'open', 'text-blue-600 dark:text-blue-400'], ['Pending', 'pending', 'text-yellow-600 dark:text-yellow-400'], ['Resolved', 'resolved', 'text-green-600 dark:text-green-400'], ['Closed', 'closed', 'text-gray-500 dark:text-gray-400']] as const).map(
            ([label, key, cls]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`bg-white dark:bg-gray-800 rounded-xl border p-4 text-left transition-colors hover:border-brand-300 dark:hover:border-brand-600 ${activeTab === key ? 'border-brand-400 dark:border-brand-600 ring-1 ring-brand-300 dark:ring-brand-700' : 'border-gray-200 dark:border-gray-700'}`}
              >
                <div className={`text-2xl font-bold ${cls}`}>{counts[key]}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</div>
              </button>
            )
          )}
        </div>
      )}

      {/* Tab bar — scrollable on mobile */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`flex-shrink-0 px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.value
                ? 'border-brand-600 text-brand-700 dark:text-brand-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
            {tab.value !== 'all' && counts && (
              <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({counts[tab.value]})</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">Loading…</div>}

      {!loading && !error && tickets.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-16 text-center">
          <Inbox size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No tickets</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'all' ? 'No support tickets yet.' : `No ${activeTab} tickets.`}
          </p>
        </div>
      )}

      {/* Table — horizontally scrollable on mobile; secondary cols hidden below sm */}
      {!loading && tickets.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left hidden sm:table-cell w-10">#</th>
                  <th className="px-4 py-3 text-left">Subject</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Submitted by</th>
                  <th className="px-4 py-3 text-left hidden lg:table-cell">Tenant Name</th>
                  <th className="px-4 py-3 text-left hidden xl:table-cell">Assigned to</th>
                  <th className="px-4 py-3 text-center hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Replies</th>
                  <th className="px-4 py-3 text-right">Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {tickets.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500 font-mono text-xs hidden sm:table-cell">{t.id}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white truncate max-w-[180px] sm:max-w-xs">{t.subject}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {/* Status shown inline on mobile only */}
                        <span className={`sm:hidden inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[t.status] ?? STATUS_BADGE.open}`}>
                          {t.status}
                        </span>
                        {t.user_reply_count > 0 && t.agent_reply_count === 0 && (
                          <span className="text-xs text-orange-500 font-medium">Awaiting reply</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-gray-900 dark:text-white">
                        {t.user_name || '—'}
                        <span className="text-gray-400 dark:text-gray-500 font-mono text-xs ml-1">[{t.user_id}]</span>
                      </div>
                      {t.user_email && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{t.user_email}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hidden lg:table-cell">{t.tenant_name || t.tenant_id}</td>
                    <td className="px-4 py-3 text-xs hidden xl:table-cell">
                      {t.assigned_to_name
                        ? <span className="text-gray-700 dark:text-gray-300">{t.assigned_to_name}</span>
                        : <span className="text-gray-400 dark:text-gray-500 italic">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[t.status] ?? STATUS_BADGE.open}`}>
                        {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400 hidden md:table-cell">{t.reply_count}</td>
                    <td className="px-4 py-3 text-right text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap">
                      {relativeTime(t.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
